import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  message: string
  type: ToastType
  duration?: number
}

interface ToastContextValue {
  toasts: Toast[]
  addToast: (message: string, type?: ToastType, duration?: number) => void
  removeToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
    const timer = timers.current.get(id)
    if (timer) { clearTimeout(timer); timers.current.delete(id) }
  }, [])

  const addToast = useCallback((message: string, type: ToastType = 'info', duration = 4000) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    setToasts((prev) => [...prev, { id, message, type, duration }])
    timers.current.set(id, setTimeout(() => removeToast(id), duration))
  }, [removeToast])

  useEffect(() => {
    return () => { timers.current.forEach((t) => clearTimeout(t)); timers.current.clear() }
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be inside ToastProvider')
  return ctx
}

const typeIcons: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  info: 'i',
  warning: '⚠',
}

const typeStyles: Record<ToastType, { border: string; bg: string; icon: string }> = {
  success: { border: 'rgba(34, 197, 94, 0.3)', bg: 'rgba(34, 197, 94, 0.06)', icon: '#22c55e' },
  error: { border: 'rgba(239, 68, 68, 0.3)', bg: 'rgba(239, 68, 68, 0.06)', icon: '#ef4444' },
  info: { border: 'rgba(56, 189, 248, 0.3)', bg: 'rgba(56, 189, 248, 0.06)', icon: '#38bdf8' },
  warning: { border: 'rgba(234, 179, 8, 0.3)', bg: 'rgba(234, 179, 8, 0.06)', icon: '#eab308' },
}

export function ToastContainer() {
  const { toasts, removeToast } = useToast()

  if (toasts.length === 0) return null

  return (
    <div className="toast-container">
      {toasts.map((toast) => {
        const s = typeStyles[toast.type]
        return (
          <div
            key={toast.id}
            className="toast"
            style={{
              borderColor: s.border,
              background: s.bg,
              animation: 'toastIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            <span
              style={{
                width: 22, height: 22, borderRadius: '50%',
                background: s.icon, color: '#0b1120',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.6875rem', fontWeight: 800, flexShrink: 0,
              }}
            >
              {typeIcons[toast.type]}
            </span>
            <span style={{ flex: 1 }}>{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              style={{
                background: 'none', border: 'none', color: 'var(--text-muted)',
                cursor: 'pointer', fontSize: '0.75rem', padding: '0.125rem',
                lineHeight: 1, opacity: 0.6, transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
            >
              ✕
            </button>
          </div>
        )
      })}
    </div>
  )
}
